namespace $ {

	let native_secret: $giper_baza_land['secret'] | null = null
	let native_sand_open: $giper_baza_land['sand_open'] | null = null

	/** Read-only compatibility for the exact pre-upgrade ciphertext inventory. */
	export class $shm_hitalama_compat_crypto extends $giper_baza_land {

		@ $mol_mem_key
		migrate_sand_open( sand: $giper_baza_unit_sand ) {
			return $mol_wire_sync( this ).sand_open( sand )
		}

		@ $mol_mem
		secret_compat() {
			if( !this.encrypted() ) return null
			const auth = this.auth()
			const gift = this._gift.get( auth.pass().lord().str )
			if( !gift ) return $mol_fail( new Error( 'Access denied' ) )
			if( !gift.code_exists() ) return $mol_fail( new Error( 'No key to decrypt' ) )
			const mutual = auth.secret_mutual( this.lord_pass( gift.lord() )! )
			if( !mutual ) return $mol_fail( new Error( "Can't decrypt secret" ) )

			// The async task arguments must keep identity across wire suspensions.
			return $mol_wire_sync( this ).secret_open_compat( mutual, gift )
		}

		async secret_open_compat( mutual: $mol_crypto_sacred, gift: $giper_baza_unit_gift ) {
			const salt = $mol_buffer.from( gift.salt().slice() )
			if( !this.$.$shm_hitalama_compat_crypto_legacy( this.link(), gift ) ) {
				salt.mix( this.link().lord().toBin() )
			}
			return new $mol_crypto_sacred( ( await mutual.open( gift.code(), salt ) ).buffer )
		}

		async sand_open_compat( sand: $giper_baza_unit_sand ): Promise< Uint8Array< ArrayBuffer > > {
			if( this.sand_get( sand.head(), sand.lord(), sand.self() ) !== sand ) {
				for( const id of this.Tine().items_vary() ?? [] ) {
					const open = await this.$.$giper_baza_glob.Land( $giper_baza_link_schema.cast( id! )! ).sand_open( sand )
					if( open ) return open
				}
				return undefined!
			}
			const secret = this.secret()
			if( sand._open ) return sand._open
			if( !sand._ball ) sand._ball = sand.big() ? await $mol_wire_async( this.mine() ).ball_load( sand ) : sand.data()
			if( secret && sand._ball && !sand.dead() ) {
				const salt = $mol_buffer.from( sand.salt().slice() )
				if( !this.$.$shm_hitalama_compat_crypto_legacy( this.link(), sand ) ) salt.mix( this.link().toBin() )
				sand._open = await secret.decrypt( sand._ball, salt )
			} else {
				sand._open = sand._ball
			}
			return sand._open!
		}

		@ $mol_mem
		encrypted_compat( next?: boolean ) {
			$mol_wire_solid()
			const gift = this._gift.get( this.link().str )
			const prev = gift?.code_exists() ?? false
			if( next === undefined ) return prev
			if( this.faces.size ) {
				if( prev === next ) return prev
				$mol_fail( new Error( 'Change encryption is forbidden' ) )
			}
			const auth = this.auth()
			const unit = $mol_wire_sync( $giper_baza_unit_gift ).make()
			$giper_baza_unit_trusted_grant( unit )
			unit.rank( $giper_baza_rank_rule )
			unit.time_tick( this.tick().time_tick )
			unit.lord( auth.pass().lord() )
			unit.mate( auth.pass().lord() )
			unit._land = this
			if( next ) {
				const secret = $mol_wire_sync( $mol_crypto_sacred ).make()
				const mutual = auth.secret_mutual( auth.pass() )
				unit.code().set( $mol_wire_sync( this ).secret_close_compat( mutual, secret, unit ) )
			}
			this.diff_apply( [ auth.pass(), unit ] )
			return next
		}

		async secret_close_compat( mutual: $mol_crypto_sacred, secret: $mol_crypto_sacred, unit: $giper_baza_unit_gift ) {
			const salt = $mol_buffer.from( unit.salt().slice() ).mix( this.link().lord().toBin() )
			return mutual.close( secret, salt )
		}
	}

	/** Keep newly created private Hitalama lands readable by current Baza. */
	export function $shm_hitalama_compat_crypto_current_install() {
		const proto = $giper_baza_land.prototype as $shm_hitalama_compat_crypto
		if( proto.encrypted === $shm_hitalama_compat_crypto.prototype.encrypted_compat ) return
		proto.secret_close_compat = $shm_hitalama_compat_crypto.prototype.secret_close_compat
		proto.encrypted = $shm_hitalama_compat_crypto.prototype.encrypted_compat
	}

	/** Enables legacy reads only while the one-time profile migration is running. */
	export function $shm_hitalama_compat_crypto_migration_install() {
		const proto = $giper_baza_land.prototype as $shm_hitalama_compat_crypto
		$shm_hitalama_compat_crypto_current_install()
		if( proto.sand_open === $shm_hitalama_compat_crypto.prototype.sand_open_compat ) return
		native_secret ??= proto.secret
		native_sand_open ??= proto.sand_open
		proto.secret_open_compat = $shm_hitalama_compat_crypto.prototype.secret_open_compat
		proto.migrate_sand_open = $shm_hitalama_compat_crypto.prototype.migrate_sand_open
		proto.secret = $shm_hitalama_compat_crypto.prototype.secret_compat
		proto.sand_open = $shm_hitalama_compat_crypto.prototype.sand_open_compat
	}

	/** Restores normal reads after all reachable legacy units were rewritten. */
	export function $shm_hitalama_compat_crypto_migration_uninstall() {
		const proto = $giper_baza_land.prototype as $shm_hitalama_compat_crypto
		if( native_secret ) proto.secret = native_secret
		if( native_sand_open ) proto.sand_open = native_sand_open
	}

	/** Rewrites this user's legacy units in one already synchronized land. */
	export function $shm_hitalama_compat_crypto_land_migrate(
		land: $giper_baza_land,
		auth: $giper_baza_auth,
	) {
		const lord = auth.pass().lord().str
		const values: $giper_baza_vary_type[] = []
		const sands: $giper_baza_unit_sand[] = []
		for( const heads of land._sand.values() ) {
			for( const peers of heads.values() ) {
				for( const sand of peers.values() ) sands.push( sand )
			}
		}

		for( const sand of sands ) {
			if( sand.dead() ) continue
			const generated = sand._vary !== undefined
			let vary = sand._vary
			if( vary === undefined ) {
				;( land as $shm_hitalama_compat_crypto ).migrate_sand_open( sand )
				vary = land.sand_decode( sand )
			}
			values.push( vary )
			if( generated ) continue
			if( sand.lord().str !== lord ) continue
			if( !land.$.$shm_hitalama_compat_crypto_legacy( land.link(), sand ) ) continue
			land.post( sand.lead(), sand.head(), sand.self(), vary, sand.tag() )
		}

		const root = land.$.$giper_baza_glob.Land( land.link().lord() )
		root.sync()
		const gift = root._gift.get( lord )
		if( gift && root.$.$shm_hitalama_compat_crypto_legacy( root.link(), gift ) ) {
			root.give( auth.pass(), gift.rank() )
		}

		return values
	}

	/** @deprecated Kept for old entry points; it no longer enables legacy reads. */
	export const $shm_hitalama_compat_crypto_install = $shm_hitalama_compat_crypto_current_install

}
